import { useState, useEffect, useCallback, FocusEventHandler } from 'react'
import Select from 'react-select'
import { Box } from 'theme-ui'
import { TMLevel } from 'types'
import theme from 'theme'
import { ChevronDownIcon } from 'components/Icon'
import { customSelectStyles } from './styles'
import { noop } from 'utils'

export type LevelSelectProps = {
  name: string
  options: TMLevel[]
  value?: TMLevel | undefined
  disabled?: boolean
  tabIndex?: string | null | undefined | number
  hasErrors?: boolean
  onChange?: (country: TMLevel) => void
  onBlur?: FocusEventHandler
  'data-cy'?: string
}

const DropdownIndicator = () => (
  <Box sx={{ width: 20, height: 20 }}>
    <ChevronDownIcon />
  </Box>
)

export const LevelSelect = ({
  name,
  value,
  options,
  tabIndex,
  disabled = false,
  hasErrors = false,
  onChange = noop,
  onBlur = noop,
  ...props
}: LevelSelectProps) => {
  const [selected, set] = useState<TMLevel | undefined>(value)
  const [focused, setFocused] = useState(false)

  useEffect(() => {
    if (value && value.value !== selected?.value) {
      set(value)
    }

    if (!value) {
      set(value)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  const handleOnChange = useCallback((level: TMLevel) => {
    set(level)
    onChange(level)
  }, [set, onChange])

  return (
    <Box
      data-cy={props['data-cy']}
      sx={{
        p: '10px',
        boxSizing: 'border-box',
        width: '100%',
        bg: 'oldlace',
        borderRadius: 8,
        boxShadow: hasErrors
          ? '0 0 0 2px #f87e0e'
          : focused
          ? '0 0 0 2px #ffc436'
          : 'none',
        opacity: disabled ? 0.6 : 1,
      }}>
      <Select
        aria-label={`select-${name}`}
        placeholder="Select a level"
        name={name}
        options={options}
        getOptionLabel={(option) => `${option.label}`}
        getOptionValue={(option) => `${option.value}`}
        value={selected || ''}
        components={{ DropdownIndicator }}
        styles={customSelectStyles}
        theme={(t) => ({
          ...t,
          colors: {
            ...t.colors,
            primary: theme.colors.primary,
            primary50: theme.colors.darkyellow,
            primary25: theme.colors.oldlace,
          },
        })}
        tabIndex={`${tabIndex}`}
        isDisabled={disabled}
        onChange={handleOnChange}
        onFocus={() => setFocused(true)}
        onBlur={(evt) => {
          onBlur(evt)
          setFocused(false)
        }}
        isSearchable
      />
    </Box>
  )
}
